import type { AuthId, UserId } from "@tento-lona/sheets";
import { CalendarId } from "@tento-lona/sheets";
import { type Option, RowId } from "@tento-lona/sheets";
import { TypedApi } from "@tento-core/typed-api";
import { Network } from "../../network.ts";
import {
  AuthScopedCalendarId,
  type AuthScopedCalendarDisplay,
  calendarWritable,
} from "../../events/calendar-types.ts";
import type { PreferencesClient } from "../../preferences/preferences-client.ts";
import { Actor, type InvalidationSink, CalendarVisibilityChanged } from "../../invalidation.ts";
import { writeCalendarVisibility } from "../../preferences/calendar-visibility-preferences.ts";
import { SdkRequestError } from "../../errors.ts";
import type { ClientLifecycle } from "../../client/handle-errors.ts";
import type { Unsubscribe } from "@tento-lona/sheets";
import { Internals } from "@tento-lona/sheets/internal-ui";

/** `CalendarsClient`'s friend record (`Internals`): its constructor registers each instance. */
type CalendarsClientInternals = {
  readonly seed: (calendars: CalendarsClient, uid: UserId, clists: Network.CalendarList[]) => void;
};
const calendarsInternals = new Internals<CalendarsClient, CalendarsClientInternals>("CalendarsClient");

/**
 * Seed `calendars` with `uid`'s calendar lists as the server already sent
 * them (a boot payload) — the load `ensureLoaded` would run, without the
 * request. Throws when the client already holds calendars (seeded, or
 * loaded by `list` / `ensureLoaded`): a seed after the load would be a
 * silent no-op, so it says so.
 *
 * @internal SDK-private: the host door `platformOf(client).seedBoot` seeds
 * through it; no barrel exports it.
 */
export function seedCalendars(calendars: CalendarsClient, uid: UserId, clists: Network.CalendarList[]): void {
  calendarsInternals.of(calendars).seed(calendars, uid, clists);
}

/**
 * A calendar the user can write events to: its account, id and name — a
 * `CalendarTarget` as it is (`client.events.create(writable, …)`).
 */
export type WritableCalendar = {
  readonly authId: AuthId;
  readonly calendarId: CalendarId;
  readonly summary: string;
};

/**
 * Fluent calendar client — combines API fetching, in-memory state, and
 * visibility management. Accessed via `client.calendar`.
 */
export class CalendarsClient {
  static readonly #internals: CalendarsClientInternals = {
    seed: (calendars, uid, clists) => {
      if (calendars.#initialized) {
        throw new Error(
          `CalendarsClient: calendars are already loaded for uid "${calendars.#uid}" — seed once, before the first load`,
        );
      }
      calendars.#install(uid, clists);
    },
  };

  static readonly ACTOR = new Actor("calendar-client");
  #uid: Option<UserId> = null;
  #authIds: Set<string> = new Set();
  #calendarLists: Network.CalendarList[] = [];
  #visibilityById: Map<string, boolean> = new Map();
  #listeners: Array<() => void> = [];
  #byAuthId: Map<
    string,
    Map<string, Network.CalendarDisplay>
  > = new Map();
  /** Whether the calendar lists are loaded (`isLoaded`); set once, by `#install`. */
  #initialized = false;
  /** The owning client's liveness: `ensureLoaded` and `list` check it. */
  readonly #lifecycle: ClientLifecycle;

  /**
   * @param currentUser Resolves the signed-in user's id — the principal
   * `list()` loads for (the client's bound user, else `GET /api/user`).
   * @param lifecycle The owning client's (`LonaSdk.Client.Lifecycle`):
   * after its `dispose`, `ensureLoaded` and `list` reject with
   * `LonaSdk.Client.DisposedError` — also a load it overtook, which then
   * installs nothing.
   */
  readonly #invalidation: InvalidationSink;
  readonly #preferences: PreferencesClient;
  readonly #api: TypedApi<Network.Routes>;
  readonly #currentUser: () => Promise<UserId>;
  constructor(
    api: TypedApi<Network.Routes>,
    preferences: PreferencesClient,
    invalidation: InvalidationSink,
    currentUser: () => Promise<UserId>,
    lifecycle: ClientLifecycle,
  ) {
    calendarsInternals.register(this, CalendarsClient.#internals);
    this.#currentUser = currentUser;
    this.#api = api;
    this.#preferences = preferences;
    this.#invalidation = invalidation;
    this.#lifecycle = lifecycle;
  }

  // ── Initialization ────────────────────────────────────────────────

  /**
   * Load the calendars from the API once. Idempotent for the same
   * `uid`; throws if the client was already loaded for a different user
   * (a `CalendarsClient` is per-user — make a new one instead).
   *
   * Throws `SdkRequestError` (boundary `calendars`, step `load`) when
   * `GET /api/calendars` is rejected or unanswered; the client then stays
   * unloaded (`isLoaded()` is `false`), so a later `ensureLoaded` retries.
   */
  async ensureLoaded(uid: UserId): Promise<void> {
    this.#lifecycle.assertLive("calendars.ensureLoaded");
    if (this.#initialized) {
      if (this.#uid !== uid) {
        throw new Error(
          `CalendarsClient.ensureLoaded: already loaded for uid "${this.#uid}", called with "${uid}"`,
        );
      }
      return;
    }
    const clists = await SdkRequestError.checkRequest(
      "calendars",
      "list",
      "GET",
      "/api/calendars",
      () => this.#api.getResult("/api/calendars"),
    );
    this.#lifecycle.assertLive("calendars.ensureLoaded");
    this.#install(uid, clists);
  }

  /**
   * Every calendar list of the signed-in user, loading them first when
   * this client has not yet (`ensureLoaded` for the current user). Throws
   * `SdkRequestError` when the user or the calendar list cannot be read.
   *
   * ```ts
   * const lists = await client.calendars.list();
   * ```
   */
  async list(): Promise<Network.CalendarList[]> {
    this.#lifecycle.assertLive("calendars.list");
    if (!this.#initialized) await this.ensureLoaded(await this.#currentUser());
    return this.#calendarLists;
  }

  /**
   * The loaded calendar lists, synchronously — for a renderer that reads
   * after the app loaded them. Throws before the first load (`list()`,
   * `ensureLoaded`, a host's `platformOf(client).seedBoot`) rather than
   * returning an empty list that
   * looks like "no calendars"; check `isLoaded()` where a pre-load render
   * is legitimate.
   */
  peekAll(): readonly Network.CalendarList[] {
    if (!this.#initialized) {
      throw new Error(
        "CalendarsClient.peekAll: calendars are not loaded yet; " +
          "await client.calendars.list() first",
      );
    }
    return this.#calendarLists;
  }

  /** Whether calendars have been loaded (via `list`, `ensureLoaded`, or a
   *  host's `platformOf(client).seedBoot`). */
  isLoaded(): boolean {
    return this.#initialized;
  }

  /** Install `uid`'s calendar lists: the one writer of the loaded state
   *  (`ensureLoaded`'s answer, or a boot seed — `seedCalendars`). LOCAL
   *  ONLY: no request. */
  #install(uid: UserId, clists: Network.CalendarList[]): void {
    if (this.#initialized) return;
    this.#initialized = true;
    this.#uid = uid;
    this.#calendarLists = clists;
    for (const clist of clists) {
      this.#authIds.add(clist.authId);
      const calendarMap = new Map<string, Network.CalendarDisplay>();
      for (const calendar of clist.calendars) {
        calendarMap.set(calendar.id, calendar);
      }
      this.#byAuthId.set(clist.authId, calendarMap);
    }
    const visibility = this.#preferences.calendarVisibility(uid).peekAll();
    for (const entry of visibility) {
      this.#visibilityById.set(entry.key, entry.visible);
    }
  }

  // ── Lookup ────────────────────────────────────────────────────────

  /** Find a calendar by auth-scoped ID. */
  peek(acid: AuthScopedCalendarId): Option<AuthScopedCalendarDisplay> {
    const [authId, calendarId] = acid;
    const calendarMap = this.#byAuthId.get(authId);
    if (!calendarMap) return null;
    const calendar = calendarMap.get(calendarId);
    if (!calendar) return null;
    return [authId, calendar];
  }

  /**
   * Find the calendar whose server-rendered events row is `rowId` — the
   * reverse of `CalendarDisplay.eventsLookupKey` (compared as interned
   * `RowId`s). Lets a caller holding only
   * a cell-write's row id (a failed write, a sync report) recover the
   * calendar's source and capabilities without parsing the key, which is
   * provider-shaped and normalized server-side.
   */
  peekByEventsRowKey(rowId: RowId): Option<AuthScopedCalendarDisplay> {
    for (const clist of this.#calendarLists) {
      for (const calendar of clist.calendars) {
        if (RowId.of(calendar.eventsLookupKey) === rowId) return [clist.authId, calendar];
      }
    }
    return null;
  }

  /** The server-flagged primary auth account (`isPrimary`); `null` when no
   *  account is flagged — no fallback (see `peekPrimaryAuthOrFirst`). */
  peekPrimaryAuth(): Option<Network.CalendarList> {
    return this.#calendarLists.find((c) => c.isPrimary) ?? null;
  }

  /** The server-flagged primary auth account, else the first account. */
  peekPrimaryAuthOrFirst(): Option<Network.CalendarList> {
    return this.peekPrimaryAuth() ?? this.#calendarLists[0] ?? null;
  }

  /**
   * The server-flagged primary calendar: in the account the server marks
   * `isPrimary`, the calendar whose id is the account's auth id. `null` when
   * no account is flagged or it holds no such calendar — no fallback. A
   * caller that wants "some calendar to write to" uses `peekPrimaryOrFirst()`.
   */
  peekPrimary(): Option<AuthScopedCalendarDisplay> {
    const auth = this.#calendarLists.find((c) => c.isPrimary);
    if (!auth) return null;
    const match = auth.calendars.find((c) => isAccountCalendar(c.id, auth.authId));
    return match ? [auth.authId, match] : null;
  }

  /**
   * The primary calendar, or the first available one: server-flagged primary
   * account (else the first account), then its id-matching calendar (else
   * its first calendar).
   */
  peekPrimaryOrFirst(): Option<AuthScopedCalendarDisplay> {
    const auth = this.peekPrimaryAuthOrFirst();
    if (!auth) return null;
    const match = auth.calendars.find((c) => isAccountCalendar(c.id, auth.authId));
    if (match) return [auth.authId, match];
    if (auth.calendars[0] != null) return [auth.authId, auth.calendars[0]];
    return null;
  }

  /** The server-flagged primary calendar's auth-scoped ID (see `peekPrimary()`). */
  peekPrimaryId(): Option<AuthScopedCalendarId> {
    const acal = this.peekPrimary();
    if (!acal) return null;
    return AuthScopedCalendarId.of(acal);
  }

  // ── Visibility ────────────────────────────────────────────────────

  /** Check if a calendar is visible (defaults to true). */
  isVisible(acid: AuthScopedCalendarId): boolean {
    const key = AuthScopedCalendarId.key(acid);
    const visible = this.#visibilityById.get(key);
    return visible == null || visible !== false;
  }

  /**
   * Set calendar visibility, a local write: stored on this device (the
   * client's preferences storage, per signed-in user; before the calendars
   * load, for this session only) and sent to no server, so nothing refuses
   * it. `actor` stamps the `CalendarVisibilityChanged` so the writing surface
   * can recognise its own echo (`Actor.EXTERNAL` for a caller with none). Throws
   * `LonaSdk.Client.DisposedError` once the owning client was disposed,
   * nothing changed.
   */
  setVisible(
    acid: AuthScopedCalendarId,
    visible: boolean,
    actor: Actor,
  ): void {
    this.#lifecycle.assertLive("calendars.setVisible");
    const key = AuthScopedCalendarId.key(acid);
    this.#visibilityById.set(key, visible);
    // Before load there is no user to persist under; the in-memory
    // override still applies for this session.
    if (this.#uid != null) {
      writeCalendarVisibility(this.#preferences.calendarVisibility(this.#uid), acid, visible);
    }
    for (const listener of this.#listeners) listener();
    this.#invalidation.emit(new CalendarVisibilityChanged(actor));
  }

  /** All calendar lists filtered to only visible calendars. */
  peekVisible(): readonly Network.CalendarList[] {
    if (!this.#calendarLists) return [];
    return this.#calendarLists.map((clist) => ({
      authId: clist.authId,
      isPrimary: clist.isPrimary,
      calendars: clist.calendars.filter((calendar) =>
        this.isVisible(AuthScopedCalendarId.of([clist.authId, calendar])),
      ),
    }));
  }

  /**
   * Every calendar the signed-in user can write events to, in list order
   * (the target picker of a create). Throws before the first load, as
   * `peekAll` does.
   */
  peekWritable(): readonly WritableCalendar[] {
    const result: WritableCalendar[] = [];
    for (const clist of this.peekAll()) {
      for (const calendar of clist.calendars) {
        if (!calendarWritable(calendar)) continue;
        result.push({
          authId: clist.authId,
          calendarId: CalendarId.of(calendar.id),
          summary: calendar.summary ?? "Untitled Calendar",
        });
      }
    }
    return result;
  }

  /** Writable calendars excluding a specific one (for "move event" UI). */
  peekWritableExcept(
    exclude: AuthScopedCalendarId,
    max: number,
  ): WritableCalendar[] {
    const [excludeAuthId, excludeCalendarId] = exclude;
    const result: WritableCalendar[] = [];
    for (const clist of this.#calendarLists) {
      for (const calendar of clist.calendars) {
        const isWritable = calendarWritable(calendar);
        const isNotExcluded =
          clist.authId !== excludeAuthId || calendar.id !== excludeCalendarId;
        if (isWritable && isNotExcluded) {
          result.push({
            authId: clist.authId,
            calendarId: CalendarId.of(calendar.id),
            summary: calendar.summary ?? "Untitled Calendar",
          });
        }
        if (result.length >= max) return result;
      }
    }
    return result;
  }

  // ── Listeners ─────────────────────────────────────────────────────

  /** Subscribe to visibility changes. Returns an unsubscribe function.
   *  Throws `LonaSdk.Client.DisposedError` once the owning client was
   *  disposed (a disposed client announces nothing). */
  onVisibilityChange(cb: () => void): Unsubscribe {
    this.#lifecycle.assertLive("calendars.onVisibilityChange");
    this.#listeners.push(cb);
    return () => {
      const idx = this.#listeners.indexOf(cb);
      if (idx !== -1) this.#listeners.splice(idx, 1);
    };
  }
}

/**
 * Whether `calendarId` is the account's own calendar: a provider keys an
 * account's primary calendar by the account id (`authId`).
 */
function isAccountCalendar(calendarId: CalendarId, authId: AuthId): boolean {
  const id: string = calendarId;
  return id === authId;
}
